import { useRef, useState, useEffect } from "react";
import { Sparkles, ArrowRight, Bot, Wand2, Play } from "lucide-react";
import { useNavigate } from "react-router-dom";

const R2 = "https://pub-626563a90a7f431c96f87c5838c120bc.r2.dev";

// Sichtbarer Beweis: 3 echte Kundenprojekte + 1 klar gekennzeichneter Demo-Spot.
// Ketterer bewusst NICHT gelistet (inoffiziell, keine Freigabe der Marke).
const PROOF = [
  { id: "ki-seedos-spot",        label: "Seedos · Kunde" },
  { id: "ki-monitoris-steuer-a", label: "Monitoris · Kunde" },
  { id: "ki-monitoris-trainer",  label: "Monitoris · Kunde" },
  { id: "ki-gold8-powder",       label: "Powder · Demo" },
];

// Kachel: Poster als Standard, Video nur bei Hover (Performance)
const ProofTile = ({ item }) => {
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || !("IntersectionObserver" in window)) { setInView(true); return; }
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) setInView(true);
        else { vidRef.current?.pause(); setPlaying(false); }
      }),
      { rootMargin: "200px" }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const start = () => { const v = vidRef.current; if (!v) return; const p = v.play(); if (p && p.catch) p.catch(() => {}); setPlaying(true); };
  const stop = () => { vidRef.current?.pause(); setPlaying(false); };

  return (
    <div
      ref={wrapRef}
      onMouseEnter={start}
      onMouseLeave={stop}
      onClick={() => (playing ? stop() : start())}
      className="group relative aspect-[9/16] rounded-lg overflow-hidden bg-black border border-white/10 hover:border-[hsl(var(--accent))]/60 transition-all cursor-pointer"
    >
      {inView ? (
        <video
          ref={vidRef}
          src={`${R2}/videos/${item.id}.mp4`}
          poster={`/thumbnails/${item.id}.webp`}
          muted loop playsInline preload="none"
          className="absolute inset-0 w-full h-full object-cover"
        />
      ) : (
        <img src={`/thumbnails/${item.id}.webp`} alt="" loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      )}
      {!playing && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-9 h-9 rounded-full bg-black/45 backdrop-blur-sm flex items-center justify-center">
            <Play className="w-3.5 h-3.5 text-white ml-0.5" fill="white" />
          </div>
        </div>
      )}
      <div className="absolute inset-x-0 bottom-0 p-1.5 bg-gradient-to-t from-black/85 to-transparent">
        <div className="text-[9px] sm:text-[10px] text-white/90 font-medium truncate">{item.label}</div>
      </div>
    </div>
  );
};

/**
 * Teaser auf der Homepage: Behauptung + sichtbarer Beweis (echte generierte Spots).
 * Verlinkt zur vollen /ki-loesungen Seite.
 */
const KiTeaser = () => {
  const navigate = useNavigate();
  return (
    <section id="ki-solutions" className="py-16 sm:py-24 bg-secondary/30" data-testid="ki-teaser-section">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto relative overflow-hidden rounded-2xl bg-gradient-to-br from-[hsl(var(--accent))]/15 via-background/40 to-background/40 border border-[hsl(var(--accent))]/30 p-8 sm:p-10">
          <div className="pointer-events-none absolute -top-20 -right-20 w-64 h-64 rounded-full bg-[hsl(var(--accent))]/20 blur-3xl" />

          <div className="relative grid lg:grid-cols-2 gap-8 lg:gap-10 items-center">
            {/* Links: Aussage */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))]/30 text-[hsl(var(--accent))] text-xs font-semibold uppercase tracking-widest mb-4">
                <Sparkles className="w-3.5 h-3.5" /> KI Lösungen
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold mb-3" data-testid="ki-teaser-title">
                Ich nutze KI nicht nur — ich baue sie.
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground mb-5">
                <span className="text-white font-semibold">freycreate.ai</span> ist meine eigene
                KI-Plattform — kein fremdes Tool-Abo. 13 Video-Modelle, dazu Bild, Voice, Musik,
                Logos und Motion Graphics. Ich produziere damit für dich — oder gebe deinem Team
                direkt Zugang, um selbst zu generieren.
              </p>
              <div className="flex flex-wrap gap-2 mb-6 text-xs text-muted-foreground">
                <span className="px-2.5 py-1 rounded-full bg-secondary/60 border border-white/5 inline-flex items-center gap-1.5">
                  <Wand2 className="w-3.5 h-3.5" /> Eigene Plattform
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary/60 border border-white/5 inline-flex items-center gap-1.5">
                  <Bot className="w-3.5 h-3.5" /> KI-Agenten
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary/60 border border-white/5">
                  13 Video-Modelle
                </span>
                <span className="px-2.5 py-1 rounded-full bg-secondary/60 border border-white/5">
                  Deutschlandweit remote
                </span>
              </div>
              <button
                onClick={() => navigate("/ki-loesungen")}
                className="group inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white font-semibold transition-all hover:scale-[1.03]"
                data-testid="ki-teaser-cta"
              >
                Alle KI-Projekte ansehen
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>

            {/* Rechts: der Beweis */}
            <div>
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--accent))] font-semibold mb-3">
                Ohne Kamera. Ohne Set.
              </div>
              <div className="grid grid-cols-4 gap-2 sm:gap-2.5">
                {PROOF.map((item) => <ProofTile key={item.id} item={item} />)}
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Kundenprojekte und ein Demo-Spot — alle auf freycreate.ai generiert. Hover für Vorschau.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default KiTeaser;
